import type { LibraryPost, LibraryPostAnswer } from './types';

/**
 * The fields `readInquiryLibraryAnswers` reads. Optional as well as nullable, because a
 * response from an API that predates inquiry library answers leaves them out entirely.
 */
export type PostWithInquiryLibraryAnswers = Partial<Pick<LibraryPost, 'question' | 'answers'>>;

/**
 * The layout a post is shown in, `answers` for the question and one card per specialist and
 * `body` for the single block of text, with its size. The counts are zero for `body`.
 */
export type LibraryPostFormat = {
  format: 'answers' | 'body';
  answerCount: number;
  /** Replies after each specialist's first, from either side, across the post. */
  replyCount: number;
  referenceCount: number;
};

/**
 * The structured form of an Inquiry Library post: the question once, then one
 * answer per specialist, each a run of replies.
 */
export type InquiryLibraryAnswers = {
  question: string;
  answers: LibraryPostAnswer[];
};

const byPosition = <T extends { position: number }>(first: T, second: T) =>
  first.position - second.position;

/**
 * The answers a post should render as, or null when it should render as its
 * body, as it always has.
 *
 * Null covers every case where the data cannot carry the answers on its own: no
 * question, no answers (the API sends `[]` for a post with no rows and for a
 * preview viewer), or answers with nothing in them. An answer with no replies
 * is dropped rather than shown as an empty block, and if that drops them all
 * the post falls back to its body. Falling back is always safe, because the
 * body holds the same text.
 *
 * Answers come back sorted by `position`, and each answer's replies too. The
 * sort is stable, so two sharing a position keep the order the API sent them
 * in. Replies are never merged: two in a row from the same role stay two.
 */
export function readInquiryLibraryAnswers(
  post: PostWithInquiryLibraryAnswers
): InquiryLibraryAnswers | null {
  const question = post.question?.trim();

  if (!question || !Array.isArray(post.answers)) {
    return null;
  }

  const answers = post.answers
    .filter((answer) => Array.isArray(answer.replies) && answer.replies.length > 0)
    .map((answer) => ({
      ...answer,
      replies: [...answer.replies].sort(byPosition),
      references: Array.isArray(answer.references) ? answer.references : [],
    }))
    .sort(byPosition);

  if (answers.length === 0) {
    return null;
  }

  return { question, answers };
}

/**
 * The layout a post is shown in, with its size, for analytics. `answers` exactly when the answers
 * view renders, which is when `readInquiryLibraryAnswers` returns answers, and `body` otherwise.
 * A reply count leaves out each answer's first reply, which is the answer itself.
 */
export function postFormatOf(answers: InquiryLibraryAnswers | null): LibraryPostFormat {
  if (!answers) {
    return { format: 'body', answerCount: 0, replyCount: 0, referenceCount: 0 };
  }

  return {
    format: 'answers',
    answerCount: answers.answers.length,
    replyCount: answers.answers.reduce(
      (count, answer) => count + Math.max(0, answer.replies.length - 1),
      0
    ),
    referenceCount: answers.answers.reduce(
      (count, answer) => count + (answer.references?.length ?? 0),
      0
    ),
  };
}
